"use client";

import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import { AnimatePresence, motion } from "motion/react";
import { AnimatedLogo } from "./animated-logo";
import { ChatBubble } from "@/components/chat/chat-bubble";
import { TextShimmer } from "@/components/motion-primitives/text-shimmer";
import { cn } from "@/lib/utils";
import { useSendMessage } from "@/hooks/use-send-message";
import { usePrompt } from "@/hooks/use-prompt";
import { imagineRoutingStore } from "@/wrapper/imagine-routing.store";
import { imagineStore } from "@/stores/imagine.store";
import { useMutation } from "@tanstack/react-query";
import unicornSceneConfig from "@/components/ui/unicorn-studio/prompt-section.json";
import { SafeUnicornScene } from "../ui/unicorn-studio/safe-unicorn-scene";
import { toast } from "sonner";
import { ContextChips } from "../prompt-composer/context-chips";
import { PromptAddonType } from "@/stores/slices/prompt-context.slice";
import {
  ComposerAttachFileButton,
  ComposerChips,
  ComposerDropzone,
  ComposerEnhancePromptButton,
  ComposerFrame,
  ComposerProvider,
  ComposerTextarea,
  ComposerToolbar,
} from "../prompt-composer";
import { Button } from "../ui/button";
import { ArrowUp, Loader2Icon } from "lucide-react";
import { ID } from "@appwrite.io/console";

export function PromptSection() {
  const { prompt, setPrompt, previousPrompt, setPreviousPrompt } = usePrompt();
  const [isTyping, setIsTyping] = useState(false);
  const typingTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const navigate = imagineRoutingStore.use.navigate();
  const sendMessage = useSendMessage();
  const isGenerating = imagineStore.use.chat.isGenerating();
  const [enhancementError, setEnhancementError] = useState<string | null>(null);
  const unicornSceneContainerRef = useRef<HTMLDivElement>(null);
  const addons = imagineStore.use.promptContext.addons();
  const attachments = useMemo(
    () => addons.filter((addon) => addon.type === PromptAddonType.ATTACHMENT),
    [addons],
  );

  const submitPrompt = useMutation({
    mutationKey: ["submit-prompt"],
    mutationFn: async ({ prompt }: { prompt: string }) => {
      const newProjectId = ID.unique();
      imagineStore.setState({ projectId: newProjectId });

      try {
        const response = await sendMessage({
          projectId: newProjectId,
          text: prompt.trim(),
        });

        if (response?.newProject) {
          imagineStore.setState({
            projectId: response.newProject.id,
            project: response.newProject,
            isGeneratingNewProject: true,
          });
        }

        navigate(
          {
            id: "project",
            props: {
              projectId: newProjectId,
              region: "fra",
              isGeneratingNewProject: Boolean(response?.newProject) || false,
            },
          },
          {
            animate: {
              promptSection: {
                delay: 400,
              },
              projectsSection: {
                delay: 0,
              },
              navigationDelay: 100,
            },
          },
        );
      } catch {
        // Toast is handled in useSubmitChatMessageMutation
      }
    },
  });

  const handleError = (error: Error | string) => {
    const errorMessage =
      error instanceof Error
        ? error.message
        : typeof error === "string"
          ? error
          : "Oops! Something went wrong. Please try again.";
    toast.error(errorMessage, {
      duration: 5000,
    });
  };

  const isLoading = isGenerating || submitPrompt.isPending;

  const error = useMemo(() => {
    return submitPrompt.error || enhancementError;
  }, [submitPrompt.error, enhancementError]);

  useEffect(() => {
    if (error) {
      handleError(error);
    }
  }, [error]);

  const logoAnimation = useMemo(() => {
    if (isLoading) return "submit";
    if (isTyping && !isLoading) return "typing";
    return "default";
  }, [isTyping, isLoading]);

  const handleSubmit = (prompt: string) => {
    submitPrompt.mutate({ prompt });
  };

  const typingIntervalRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const simulateTyping = useCallback((value: string) => {
    // Clear any existing typing animation
    if (typingIntervalRef.current) {
      clearInterval(typingIntervalRef.current);
    }

    let currentIdx = 0;
    setPrompt("");
    typingIntervalRef.current = setInterval(() => {
      currentIdx++;
      if (currentIdx > value.length) {
        if (typingIntervalRef.current) {
          clearInterval(typingIntervalRef.current);
          typingIntervalRef.current = null;
        }
        return;
      }
      setPrompt(value.slice(0, currentIdx));
    }, 5);
  }, []);

  // Cleanup typing interval on unmount
  useEffect(() => {
    return () => {
      if (typingIntervalRef.current) {
        clearInterval(typingIntervalRef.current);
      }
    };
  }, []);

  const handlePromptChange = (value: string) => {
    setPrompt(value);
    if (typingTimeoutRef.current) {
      clearTimeout(typingTimeoutRef.current);
    }

    typingTimeoutRef.current = setTimeout(() => {
      setIsTyping(false);
    }, 1000);
  };

  useEffect(() => {
    if (unicornSceneContainerRef.current) {
      const canvas = unicornSceneContainerRef.current.querySelector("canvas");
      if (canvas) {
        canvas.style.position = "absolute";
        canvas.style.left = "0";
        canvas.style.right = "0";
        canvas.style.width = "100%";
        canvas.style.height = "100%";
        canvas.style.bottom = "0px";
      }
    }
  }, [prompt]);
  return (
    <div className="dark">
      <div
        className={cn(
          "flex flex-col items-center bg-neutral-950 rounded-2xl relative w-full mx-auto overflow-hidden",
          "h-[600px]",
          "md:h-[730px]",
          "lg:h-[810px]",
        )}
      >
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4, delay: 0.5 }}
          className="absolute bottom-0 left-0 right-0 h-full w-full z-0 bg-neutral-950 rounded-2xl overflow-hidden"
          ref={unicornSceneContainerRef}
        >
          <SafeUnicornScene
            rawJson={unicornSceneConfig}
            width="100%"
            height="120%"
          />
        </motion.div>
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: 20 }}
          transition={{ duration: 0.4, delay: 1 }}
          className={cn(
            "flex flex-col items-center h-full w-full z-10",
            "mt-[140px] md:mt-[200px] lg:mt-[250px]",
          )}
        >
          <AnimatedLogo size={90} animation={logoAnimation} forceTheme="dark" />
          <h1 className="text-2xl font-light mt-6 tracking-tight leading-tight mb-6 text-neutral-300">
            What do you imagine?
          </h1>

          <div className="relative w-full min-h-[200px] px-[24px] md:px-0">
            <AnimatePresence>
              {!isLoading && (
                <motion.form
                  key="prompt-form"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{
                    duration: 0.3,
                    ease: "easeInOut",
                  }}
                  onSubmit={(e) => {
                    e.preventDefault();
                    if (prompt) {
                      handleSubmit(prompt);
                    }
                  }}
                  className="flex w-full items-start justify-center gap-2"
                >
                  <div className="z-10 space-y-4 w-full">
                    <div className="w-full max-w-130 mx-auto z-10">
                      <ComposerProvider
                        prompt={prompt}
                        onPromptChange={handlePromptChange}
                        previousPrompt={previousPrompt}
                        onPreviousPromptChange={setPreviousPrompt}
                      >
                        <div className="border border-border/50 rounded-2xl bg-gradient-to-b from-[#2D2D31] to-[#2D2D31]/60 backdrop-blur-[8px] px-0.5 pt-0.5 pb-2">
                          <div className="mb-2">
                            <ComposerFrame>
                              <ComposerDropzone>
                                <ComposerChips />
                                <ComposerTextarea
                                  className="min-h-[5lh] max-h-[8lh] lg:max-h-[12lh]"
                                  placeholder="Ask Imagine..."
                                  disabled={isLoading}
                                />
                                <ComposerToolbar>
                                  <div className="flex items-center gap-2">
                                    <ComposerEnhancePromptButton
                                      disabled={isLoading}
                                    />
                                  </div>
                                  <div className="flex items-center gap-2">
                                    <ComposerAttachFileButton />
                                    <Button
                                      type="submit"
                                      disabled={
                                        !prompt ||
                                        isLoading ||
                                        attachments.some(
                                          (attachment) =>
                                            attachment.props.uploadStatus ===
                                            "uploading",
                                        )
                                      }
                                      className="flex h-6.5 w-6.5 items-center justify-center rounded-md transition-all"
                                    >
                                      {isLoading ? (
                                        <Loader2Icon className="size-4 animate-spin" />
                                      ) : (
                                        <ArrowUp className="size-4" />
                                      )}
                                    </Button>
                                  </div>
                                </ComposerToolbar>
                              </ComposerDropzone>
                            </ComposerFrame>
                          </div>
                          <div className="flex items-center gap-1 flex-wrap px-2">
                            {[
                              {
                                label: "Fitness tracker",
                                prompt:
                                  "Build a cross-platform fitness tracker that logs workouts, tracks goals, visualizes progress charts, and supports social sharing and reminders.",
                              },
                              {
                                label: "SaaS landing page",
                                prompt:
                                  "Design a modern SaaS landing page featuring a compelling hero, feature grid, testimonials, pricing tiers, FAQ, and a prominent signup CTA.",
                              },
                              {
                                label: "Portfolio website",
                                prompt:
                                  "Craft a Scandinavian-inspired photographer portfolio website to showcase projects and experience, an bio, contact form, and smooth navigation.",
                              },
                              {
                                label: "Financial dashboard",
                                prompt:
                                  "Create a responsive financial analytics dashboard with revenue, expenses, cash-flow charts, KPI cards, and filterable transaction tables.",
                              },
                            ].map((suggestion) => (
                              <button
                                key={suggestion.label}
                                type="button"
                                onClick={() => {
                                  setPrompt("");
                                  simulateTyping(suggestion.prompt);
                                }}
                                disabled={isLoading}
                                className={cn(
                                  "inline-flex cursor-pointer h-6 items-center gap-1.5 rounded-md border border-border/60 bg-background/80 px-2.5 text-left text-[11px] font-normal text-foreground/65 shadow-sm backdrop-blur transition-colors relative overflow-hidden",
                                  isLoading && "cursor-not-allowed opacity-60",
                                  !isLoading &&
                                    "hover:border-foreground/20 hover:text-foreground [&:hover]:before:opacity-100",
                                  "before:absolute before:inset-0 before:bg-gradient-to-b before:from-white/[0.04] before:to-white/[0.01] before:opacity-0 before:transition-opacity",
                                )}
                              >
                                <span className="relative z-10">
                                  {suggestion.label}
                                </span>
                              </button>
                            ))}
                          </div>
                        </div>
                      </ComposerProvider>
                    </div>
                  </div>
                </motion.form>
              )}
              {isLoading && (
                <motion.div
                  key="loading-state"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="absolute inset-0 flex w-full flex-col items-center justify-start space-y-6"
                >
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: 0.55 }}
                    className="w-140 pl-20 space-y-2"
                  >
                    <ChatBubble
                      role="user"
                      className="max-h-[8lh] truncate whitespace-pre-wrap break-words"
                    >
                      {prompt
                        .trim()
                        .split("\n")
                        .map((line, idx) => (
                          <React.Fragment key={idx}>
                            {line}
                            <br />
                          </React.Fragment>
                        ))}
                    </ChatBubble>
                    {attachments.length > 0 && (
                      <div className="flex items-center flex-wrap gap-2 min-w-0 justify-end">
                        <ContextChips
                          addons={attachments.map((a) => ({
                            ...a,
                            props: { ...a.props, removable: false },
                          }))}
                        />
                      </div>
                    )}
                  </motion.div>
                  <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.25, delay: 0.85 }}
                    className="w-140"
                  >
                    <TextShimmer className="font-mono text-sm">
                      Thinking...
                    </TextShimmer>
                  </motion.div>
                </motion.div>
              )}
            </AnimatePresence>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
